import { useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "../config/api-client";
import { API_ENDPOINTS } from "../config/api-endpoints";
import { QueryKeys } from "../config/query-keys";
import type {
  LLMModelsResponse,
  LLMModelsRawResponse,
} from "../models/api-types";
import mixpanel from "mixpanel-browser";

interface UseLLMModelsOptions {
  limit?: number;
  provider_type?: string;
  searchTerm?: string;
  enabled?: boolean;
  sortBy?: string;
  brand?: string;
  category?: string;
  subCategory?: string;
}

// Infinite hook for LLM models endpoint with server-side pagination
export const useLLMModels = (options: UseLLMModelsOptions = {}) => {
  const {
    limit = 20,
    provider_type = "llm",
    searchTerm = "",
    enabled = true,
    brand,
    category,
    subCategory,
    sortBy = "",
  } = options;

  // Sort değiştiğinde fresh data için staleTime'ı 0 yap
  const hasSorting = sortBy && sortBy !== "";
  const staleTime = hasSorting ? 0 : 5 * 60 * 1000; // Sort varsa 0, yoksa 5 dakika

  return useInfiniteQuery({
    queryKey: [
      ...QueryKeys.CONVERSATIONS.all,
      "llm-models",
      { provider_type, limit, searchTerm, brand, category, subCategory, sortBy },
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }): Promise<LLMModelsResponse> => {
      const searchParams = new URLSearchParams({
        page: String(pageParam),
        limit: String(limit),
      });

      if (provider_type) {
        searchParams.append("provider_type", provider_type);
      }

      if (searchTerm.trim()) {
        const searchQuery = searchTerm.trim();
        mixpanel.track("model_searched", {
          search_query: searchQuery,
        });
        window.netmera.push((api) => {
          api.sendEvent({
            code: "ylued",
            ea: searchQuery,
          });
        });
        // Backend yeni gereksinim: searchTerm yerine search parametresi
        searchParams.append("search", searchQuery);
      }

      if (brand) {
        searchParams.append("brand", brand);
      }

      if (category) {
        searchParams.append("category", category);
      }

      if (subCategory) {
        searchParams.append("subcategory", subCategory);
      }

      if (sortBy) {
        const isDesc = sortBy.startsWith("-");
        searchParams.append("sortBy", isDesc ? sortBy.slice(1) : sortBy);
        searchParams.append("order", isDesc ? "desc" : "asc");
      }

      // Backend yeni şema ile meta içeren response döner. Ortam değişkeni
      // eksikse Axios göreli URL ile Vite index.html'i döndürebilir; bunu
      // başarılı model verisi gibi React Query'ye vermek uygulamayı çökertir.
      const raw = await apiClient.get<unknown>(
        `${API_ENDPOINTS.getAllLLMModels}?${searchParams.toString()}`,
      );

      const response = raw as Partial<LLMModelsRawResponse> | null;
      if (
        !response ||
        typeof response !== "object" ||
        !response.data ||
        typeof response.data !== "object" ||
        !response.data.meta
      ) {
        throw new Error("LLM models API returned an invalid response");
      }

      return response as LLMModelsResponse;
    },
    getNextPageParam: (lastPage: LLMModelsResponse) => {
      const meta = lastPage?.data?.meta;
      if (
        !meta ||
        !Number.isFinite(meta.page) ||
        !Number.isFinite(meta.totalPage)
      ) {
        return undefined;
      }

      const { page, totalPage } = meta;
      const nextPage = page + 1;
      return nextPage <= totalPage ? nextPage : undefined;
    },
    enabled,
    staleTime, // Dinamik staleTime
    gcTime: 10 * 60 * 1000, // 10 dakika
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
